import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, Button, Card, Heading } from '@openroad/ui';
import { api, formatMoney, newZealandDateOf, unwrap } from '../lib/api';

/**
 * Every route with rides coming up, rather than the six the home page teases.
 *
 * The home page asks for a handful; this asks for the maximum the endpoint
 * offers. Both read the same numbers, so a route cannot say one thing here and
 * another there.
 */
const MOST_THE_API_WILL_GIVE = 12;

export function PopularRoutesScreen() {
  const navigate = useNavigate();

  const popular = useQuery({
    queryKey: ['popular-routes', MOST_THE_API_WILL_GIVE],
    queryFn: () =>
      unwrap(
        api.GET('/trips/popular-routes', {
          params: { query: { limit: MOST_THE_API_WILL_GIVE } },
        }),
      ),
    staleTime: 5 * 60 * 1000,
  });

  /**
   * The day of a ride that exists, not today.
   *
   * `/trips/search` answers for one New Zealand calendar day, while these cards
   * count seven of them. Sending "4 rides this week" to today's date lands on
   * an empty result most of the time — the card was not wrong, the link was.
   * `sampleTrip` is a real ride on the route, so its day always has one.
   */
  function search(route: {
    origin: { name: string };
    destination: { name: string };
    sampleTrip: { departureAt: string };
  }) {
    const query = new URLSearchParams({
      from: route.origin.name,
      to: route.destination.name,
      date: newZealandDateOf(route.sampleTrip.departureAt),
      seats: '1',
    });
    navigate(`/search?${query.toString()}`);
  }

  return (
    <main className="mx-auto max-w-[1000px] px-6 pt-12 pb-16">
      <Heading level="page" as="h1" className="mb-2">
        Popular routes
      </Heading>
      <p className="m-0 mb-8 text-body-lg leading-relaxed text-muted">
        Where people are actually driving this week. Counts are rides leaving in the next seven days
        that still have a seat, so a route drops off this page when it fills up.
      </p>

      {popular.isLoading ? <p className="text-body text-muted">Counting the rides…</p> : null}

      {popular.isError ? (
        <Card radius="panel" className="p-7 text-center">
          <Heading level="card" as="h2" className="mb-2">
            We could not load the routes
          </Heading>
          <p className="m-0 mb-6 text-base text-muted">
            Something went wrong on our side. Try again in a moment.
          </p>
          <Button variant="cta" onClick={() => void popular.refetch()}>
            Try again
          </Button>
        </Card>
      ) : null}

      {popular.isSuccess && popular.data.items.length === 0 ? (
        <Card radius="panel" className="p-7">
          <p className="m-0 text-base text-muted">
            Nothing is running this week yet. That is a real answer rather than an empty page —{' '}
            <Link to="/publish">offer a ride</Link> and it will be the first one here.
          </p>
        </Card>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(popular.data?.items ?? []).map((route) => (
          <Card
            key={`${route.origin.id}-${route.destination.id}`}
            lift="route"
            onClick={() => search(route)}
          >
            <div className="mb-3.5 flex items-center gap-2.5">
              <span className="font-display text-title-xs font-bold">{route.origin.name}</span>
              <ArrowRightIcon size={16} className="text-pohutukawa" />
              <span className="font-display text-title-xs font-bold">{route.destination.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-meta text-muted">
                {route.ridesThisWeek} {route.ridesThisWeek === 1 ? 'ride' : 'rides'} this week
              </span>
              <span className="text-label">
                from{' '}
                <span className="font-extrabold text-ngahere">
                  {formatMoney(route.lowestPrice)}
                </span>
              </span>
            </div>
          </Card>
        ))}
      </div>
    </main>
  );
}
